<script lang="ts">
	import type { WithElementRef } from "bits-ui";
	import type { HTMLAttributes } from "svelte/elements";
	import { Button } from "#lib/web-ui/ui/button/index.js";
	import { Label } from "#lib/web-ui/ui/label/index.js";
	import { Input } from "#lib/web-ui/ui/input/index.js";
	import { Textarea } from "#lib/web-ui/ui/textarea/index.js";
	import SuperDebug, { superForm } from "sveltekit-superforms";
	import type { SuperValidated, Infer } from "sveltekit-superforms";
	import { Checkbox } from "#lib/web-ui/ui/checkbox/index.js";
	import { dev } from "$app/env";
	import { appConfig } from "#lib/app-config.js";
	import { TurnstileField } from "#lib/web-ui/forms/turnstile/index.js";

	import type { ContactFormSchema } from "./form-schema";
	import { toast } from "svelte-sonner";

	let {
		ref = $bindable(null),
		contactForm
	}: WithElementRef<HTMLAttributes<HTMLDivElement>> & {
		contactForm: SuperValidated<Infer<ContactFormSchema>>;
	} = $props();

	const { form, errors, enhance, delayed, constraints } = superForm(contactForm, {
		onResult: ({ result }) => {
			if (result.status === 200) {
				toast.success("Message sent successfully!");
			} else {
				$form.turnstileToken = "";

				if (result.type === "failure" && result.data?.turnstileError) {
					toast.error(result.data.turnstileError);
				} else {
					toast.error("Failed to send message");
				}
				console.error(result);
			}
		},
		onUpdate: ({ form, result }) => {
			if (result.type === "failure") form.data.turnstileToken = "";
		}
	});

	let services = $state({
		sellMyProperty: false,
		purchaseAProperty: false,
		investmentOpportunities: false,
		humanResources: false,
		marketingOpportunities: false,
		accountingAndFinance: false,
		other: false,
		leasingOpportunities: false
	});
	$effect(() => {
		$form.services = Object.keys(services).filter(
			(key) => services[key as keyof typeof services]
		);
	});
</script>

<div>
	<form method="POST" class="space-y-4" use:enhance action="?/submitContactForm">
		<div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
			<div class="flex h-[74px] flex-col gap-1">
				<Label for="firstName" class="text-sm">First Name</Label>
				<Input
					name="firstName"
					id="firstName"
					autocomplete="given-name"
					bind:value={$form.firstName}
					placeholder="First Name"
					{...$constraints.firstName}
					onfocusout={(e: FocusEvent) => {
						let name = (e.target as HTMLInputElement).value.toLowerCase();
						$form.firstName = name.replace(/\b\w/g, (char) => char.toUpperCase());
					}}
				/>
				{#if $errors.firstName}
					<p class="text-xs text-red-500">{$errors.firstName}</p>
				{/if}
			</div>

			<div class="flex h-[74px] flex-col gap-1">
				<Label for="lastName" class="text-sm">Last Name</Label>
				<Input
					name="lastName"
					id="lastName"
					autocomplete="family-name"
					bind:value={$form.lastName}
					placeholder="Last Name"
					{...$constraints.lastName}
					onfocusout={(e: FocusEvent) => {
						let name = (e.target as HTMLInputElement).value.toLowerCase();
						$form.lastName = name.replace(/\b\w/g, (char) => char.toUpperCase());
					}}
				/>
				{#if $errors.lastName}
					<p class="text-xs text-red-500">{$errors.lastName}</p>
				{/if}
			</div>
		</div>

		<div class="space-y-1">
			<Label for="company" class="text-sm"
				>Company <span class="text-muted-foreground text-xs italic">(if applicable)</span
				></Label
			>
			<Input
				name="company"
				id="company"
				autocomplete="organization"
				bind:value={$form.company}
				placeholder="Company Name"
				{...$constraints.company}
			/>
			{#if $errors.company}
				<p class="text-xs text-red-500">{$errors.company}</p>
			{/if}
		</div>

		<div class="space-y-1">
			<Label for="email" class="text-sm">Email</Label>
			<Input
				name="email"
				id="email"
				type="email"
				autocomplete="email"
				bind:value={$form.email}
				onfocusout={(e: FocusEvent) => {
					$form.email = (e.target as HTMLInputElement).value.toLowerCase();
				}}
				placeholder="your@email.com"
				{...$constraints.email}
			/>
			{#if $errors.email}
				<p class="text-xs text-red-500">{$errors.email}</p>
			{/if}
		</div>

		<div class="space-y-1">
			<Label for="phone" class="text-sm">Phone number</Label>
			<div class="flex">
				<Input
					name="phone"
					id="phone"
					type="tel"
					bind:value={$form.phone}
					onfocusout={(e: FocusEvent) => {
						let phone = (e.target as HTMLInputElement).value;
						phone = phone.replace(/\D/g, "");
						if (phone.length === 10) {
							phone = `(${phone.substring(0, 3)}) ${phone.substring(3, 6)}-${phone.substring(6, 10)}`;
						}
						$form.phone = phone;
					}}
					placeholder="(123) 456-7890"
					autocomplete="tel"
					{...$constraints.phone}
				/>
				{#if $errors.phone}
					<p class="text-xs text-red-500">{$errors.phone}</p>
				{/if}
			</div>
		</div>

		<div class="space-y-1">
			<Label for="content" class="text-sm">Message</Label>
			<Textarea
				name="content"
				placeholder="Leave us a message..."
				rows={3}
				bind:value={$form.content}
				{...$constraints.content}
			/>
			{#if $errors.content}
				<p class="text-xs text-red-500">{$errors.content}</p>
			{/if}
		</div>

		<div class="space-y-2">
			<p class="text-sm font-medium">Services</p>

			<div class="grid grid-cols-2 gap-2 sm:grid-cols-3">
				{#each $form.services ?? [] as service (service)}
					<input type="hidden" name="services" value={service} />
				{/each}
				<div class="flex items-center space-x-1">
					<Checkbox id="sell-my-property" bind:checked={services.sellMyProperty} />
					<Label for="sell-my-property" class="text-xs font-normal"
						>Sell my Property</Label
					>
				</div>

				<div class="flex items-center space-x-1">
					<Checkbox id="purchase-a-property" bind:checked={services.purchaseAProperty} />
					<Label for="purchase-a-property" class="text-xs font-normal"
						>Purchase a Prooperty</Label
					>
				</div>

				<div class="flex items-center space-x-1">
					<Checkbox
						id="investment-opportunities"
						bind:checked={services.investmentOpportunities}
					/>
					<Label for="investment-opportunities" class="text-xs font-normal"
						>Investment Offerings</Label
					>
				</div>

				<div class="flex items-center space-x-1">
					<Checkbox id="human-resources" bind:checked={services.humanResources} />
					<Label for="human-resources" class="text-xs font-normal">Human Resources</Label>
				</div>

				<div class="flex items-center space-x-1">
					<Checkbox
						id="marketing-opportunities"
						bind:checked={services.marketingOpportunities}
					/>
					<Label for="marketing-opportunities" class="text-xs font-normal"
						>Marketing Opportunities</Label
					>
				</div>

				<div class="flex items-center space-x-1">
					<Checkbox
						id="accounting-and-finance"
						bind:checked={services.accountingAndFinance}
					/>
					<Label for="accounting-and-finance" class="text-xs font-normal"
						>Accounting & Finance</Label
					>
				</div>

				<div class="flex items-center space-x-1">
					<Checkbox
						id="leasing-opportunities"
						bind:checked={services.leasingOpportunities}
					/>
					<Label for="leasing-opportunities" class="text-xs font-normal"
						>Leasing Opportunities</Label
					>
				</div>

				<div class="flex items-center space-x-1">
					<Checkbox id="other" bind:checked={services.other} />
					<Label for="other" class="text-xs font-normal">Other</Label>
				</div>
			</div>
		</div>
		<TurnstileField bind:token={$form.turnstileToken} class="mb-4" />
		<Button type="submit" disabled={$delayed} class="mt-4 h-9 w-full px-4 py-1 sm:w-auto"
			>Send message</Button
		>
	</form>
	{#if dev && appConfig.displayFormDebug}
		<SuperDebug data={$form} />
	{/if}
</div>
